import setActiveTopMenuDropdownOperation from 'editor/src/store/app/operation/setActiveTopMenuDropdownOperation';
import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import { removeMediaElementAction, updateMediaElementAction } from 'editor/src/store/design/slice';
import { ElementAddress } from 'editor/src/store/design/types';
import { getSharedTextFieldMembers } from 'editor/src/store/design/util/sharedTextFields';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

const removeMediaElementOperation =
  (elementAddress: ElementAddress, saveStep = false) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    batch(() => {
      dispatch(setActiveTopMenuDropdownOperation(undefined));
      if (saveStep) {
        dispatch(saveUndoRedoStateOperation('Element remove'));
      }
      const state = getState();
      const element = getMediaElement(state, elementAddress);

      // A shared group of one shares nothing. Deleting the second-to-last
      // member has to take the id off the survivor as well, or the panel dead
      // ends: "Same value as" shows None because the partner is gone, while the
      // picklist select stays closed telling the author to stop sharing first —
      // which they cannot do, since None is already what is selected.
      // updateSharedTextFieldOperation does this when a member LEAVES; deleting
      // one is the same departure by another route.
      const sharedFieldId = element?.type === 'text' ? element.extra.sharedFieldId : undefined;
      const survivors = sharedFieldId
        ? getSharedTextFieldMembers(state.design.designData, sharedFieldId).filter(
            (member) => member.element.uuid !== element?.uuid,
          )
        : [];

      // Before the removal, deliberately. The survivor's address was read from
      // the design as it stands now, and the splice below shifts everything
      // after it on the same page down one — so writing first is what makes the
      // address right, with no arithmetic to get wrong. Both land in the same
      // batch and the same undo entry either way, and an index that is off by
      // one here would not fail loudly: updateMediaElementReducer assigns by
      // position, so it would quietly rewrite a different element's extra.
      if (survivors.length === 1) {
        const [last] = survivors;
        dispatch(
          updateMediaElementAction({
            elementAddress: last.address,
            elementUpdate: { extra: { ...last.element.extra, sharedFieldId: undefined } },
          }),
        );
      }

      dispatch(removeMediaElementAction(elementAddress));

      switch (element?.type) {
        case 'addon':
          sendPostMessage('log.addonRemoved', element);
          break;
        case 'image':
          sendPostMessage('log.imageRemoved', element);
          break;
        case 'text':
          sendPostMessage('log.textRemoved', element);
          break;
        case 'line':
          sendPostMessage('log.lineRemoved', element);
          break;
        default:
          break;
      }
    });
  };

export default removeMediaElementOperation;
